// Aba "Linha do tempo" do detalhe do contrato.
// Wave 2 (Antd) — 2026-06-01. Usa Timeline Antd nativo.

import { useState } from 'react';
import axios from 'axios';
import {
  Timeline as AntTimeline, Input, Button, Empty, Card, Space, Typography,
  Popconfirm, App as AntApp,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import type { Contrato } from './types';
import { getToken } from './utils';

const { TextArea } = Input;

const ICONES: Record<string, { i: string; cor: string; label: string }> = {
  contrato_criado:    { i: '🚀', cor: '#7C3AED', label: 'Contrato iniciado' },
  status_alterado:    { i: '🔄', cor: '#0930D0', label: 'Status alterado' },
  nota:               { i: '💬', cor: '#0930D0', label: 'Nota' },
  kickoff_concluido:  { i: '🎯', cor: '#0a7f4a', label: 'Kick-off' },
  art_emitida:        { i: '📋', cor: '#B07500', label: 'ART emitida' },
  documento_subido:   { i: '📁', cor: '#0930D0', label: 'Documento' },
  atividade_concluida:{ i: '✓', cor: '#0a7f4a', label: 'Atividade' },
  atestado_gerado:    { i: '📜', cor: '#7C3AED', label: 'Atestado' },
};

export function Timeline({ contrato, eventos, onAtualizar }: {
  contrato: Contrato; eventos: any[]; onAtualizar: () => void;
}) {
  return <AntApp><TimelineInner contrato={contrato} eventos={eventos} onAtualizar={onAtualizar} /></AntApp>;
}

function TimelineInner({ contrato, eventos, onAtualizar }: {
  contrato: Contrato; eventos: any[]; onAtualizar: () => void;
}) {
  const { message } = AntApp.useApp();
  const [novoTexto, setNovoTexto] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState<string | null>(null);

  async function registrar() {
    if (!novoTexto.trim()) return;
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_registrar_evento', contrato_id: contrato.id,
          tipo: 'nota', descricao: novoTexto.trim() },
        { headers: { Authorization: token } });
      setNovoTexto('');
      await onAtualizar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  async function excluirEvento(ev: any) {
    setExcluindo(ev.id);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_excluir_evento', id: ev.id },
        { headers: { Authorization: token } });
      await onAtualizar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setExcluindo(null); }
  }

  const timelineItems = eventos.map((ev: any) => {
    const cfg = ICONES[ev.tipo] || { i: '•', cor: '#888', label: ev.tipo };
    return {
      key: ev.id,
      dot: <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 28, height: 28, borderRadius: 14,
        background: `${cfg.cor}22`, color: cfg.cor, fontSize: 14, fontWeight: 600,
      }}>{cfg.i}</span>,
      // `minWidth: 0` + `flexShrink: 0`: item de flex não encolhe abaixo do
      // próprio conteúdo, então uma nota com um token sem espaço (caminho de
      // arquivo, link colado) empurra a lixeira para fora. Medido: numa faixa de
      // ~300px bastam 34 caracteres contínuos; no desktop, 132. O `overflowWrap`
      // é necessário junto — sem ele o minWidth só transfere o estouro para
      // dentro do bloco, e o texto passa por baixo do botão.
      children: (
        <div style={{ display: 'flex', alignItems: 'start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography.Text style={{ fontSize: 12, whiteSpace: 'pre-wrap',
                                      overflowWrap: 'anywhere' }}>
              {ev.descricao}
            </Typography.Text>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                {cfg.label} · {new Date(ev.criado_em).toLocaleString('pt-BR')}
                {ev.criado_por && ` · ${ev.criado_por}`}
              </Typography.Text>
            </div>
          </div>
          <Popconfirm title="Apagar este evento?" okText="Apagar" cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            onConfirm={() => excluirEvento(ev)}>
            <Button size="small" type="text" danger icon={<DeleteOutlined />}
              style={{ flexShrink: 0 }}
              loading={excluindo === ev.id} title="Apagar evento da linha do tempo" />
          </Popconfirm>
        </div>
      ),
    };
  });

  return (
    <div>
      <Card size="small" style={{ marginBottom: 18, background: '#FAFBFF' }}
        styles={{ body: { padding: 10 } }}>
        <Space.Compact style={{ width: '100%' }}>
          <TextArea value={novoTexto} onChange={e => setNovoTexto(e.target.value)}
            placeholder="Registrar nota, marco, observação..."
            autoSize={{ minRows: 2, maxRows: 4 }} />
          <Button type="primary" onClick={registrar}
            disabled={salvando || !novoTexto.trim()} loading={salvando}
            style={{ height: 'auto' }}>
            Registrar
          </Button>
        </Space.Compact>
      </Card>

      {eventos.length === 0 ? (
        <Empty description="Sem eventos ainda. O primeiro evento foi a criação do contrato (não persistido aqui)." />
      ) : (
        <AntTimeline items={timelineItems} mode="left" />
      )}
    </div>
  );
}
